import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const CANVAS = "#f6f6f4";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const LAST_UPDATED = "April 26, 2026";

const TERMS = {
  kicker: "Terms",
  title: "Terms of Service",
  lede:
    "The agreement that covers using Interview Lab. Plain English where we can manage it.",
  sections: [
    {
      id: "acceptance",
      label: "Using the service",
      body: [
        "By creating an account, signing in with Google, or otherwise using Interview Lab, you agree to these terms. If you don't agree with something here, don't use the service.",
        "We may update these terms over time. If we change something material, we'll let you know in-app or by email. The 'last updated' date at the top tells you when the latest revision landed."
      ]
    },
    {
      id: "account",
      label: "Your account",
      body: [
        "You need an account to record answers and receive feedback. You're responsible for what happens under your account, including keeping your password private and not sharing access with other people.",
        "You must be at least 13 years old to create an account. If you're using the service on behalf of an organization, you confirm you have the authority to bind that organization to these terms."
      ]
    },
    {
      id: "subscription",
      label: "Free and Pro plans",
      body: [
        "The Free plan lets you run up to ten feedback loops a day at no cost. The Pro plan removes the daily ceiling for $9.99 per month, billed monthly through Stripe.",
        "Pro renews automatically each month until you cancel. You can cancel anytime from your profile; cancellation takes effect at the end of the current billing period and you keep Pro access until then. We don't issue refunds for partial months unless legally required.",
        "We may change pricing in the future. Existing subscribers will be notified before any change affects them and will have the chance to cancel before the new price applies."
      ]
    },
    {
      id: "use",
      label: "Acceptable use",
      body: [
        "Use the service to practice interviews. Don't use it to generate or transcribe content that's illegal, hateful, harassing, or designed to deceive other people. Don't try to scrape the service, reverse-engineer it, overload it, or use it to train another AI system.",
        "We can suspend or terminate accounts that violate these rules, with or without notice depending on severity."
      ]
    },
    {
      id: "ai",
      label: "AI-generated feedback",
      body: [
        "Feedback is generated by third-party AI models (currently OpenAI's GPT-4 for written feedback and Whisper for transcription). The output is automated and can be wrong, biased, or incomplete.",
        "Treat feedback as practice material, not professional career advice. We don't guarantee the feedback you receive will help you get any specific job, pass any specific interview, or be accurate in any specific way."
      ]
    },
    {
      id: "content",
      label: "Your recordings and transcripts",
      body: [
        "Anything you record, transcribe, edit, or save belongs to you. We process it to provide the service (transcription, feedback generation, storage) and to improve the product.",
        "We do not sell your recordings or transcripts. See the Privacy Policy for the full picture of what we collect, where it goes, and how to delete it."
      ]
    },
    {
      id: "termination",
      label: "Ending the agreement",
      body: [
        "You can stop using the service and delete your account at any time. Once your account is deleted, we remove your data on a reasonable schedule, with the exceptions called out in the Privacy Policy (legal holds, billing records, anonymized analytics).",
        "We can suspend or terminate your access if you violate these terms or if continuing to provide the service to you would create legal or security risks."
      ]
    },
    {
      id: "disclaimer",
      label: "Disclaimers and liability",
      body: [
        "The service is provided 'as is' and 'as available'. We don't warrant that it will be uninterrupted, error-free, or fit for any particular purpose.",
        "To the extent allowed by law, our total liability for any claim arising from the service is limited to the amount you paid us in the twelve months before the claim. We're not liable for indirect, incidental, or consequential damages."
      ]
    },
    {
      id: "contact",
      label: "Contact",
      body: [
        "Questions about these terms? Email us at hello@interviewlab.co. We'll get back to you."
      ]
    }
  ]
};

const PRIVACY = {
  kicker: "Privacy",
  title: "Privacy Policy",
  lede:
    "What we collect, what we do with it, and what control you have. The short version is at the top.",
  sections: [
    {
      id: "summary",
      label: "The short version",
      body: [
        "We collect your account info, the audio you record, the transcripts and feedback that come from it, and your payment details if you upgrade to Pro. We use that data to run the service. We share it with the third-party providers we depend on (OpenAI, Stripe, AWS, Google), and nowhere else. You can delete your account and your data anytime."
      ]
    },
    {
      id: "what",
      label: "What we collect",
      body: [
        "Account info: your username, name, email, and an encrypted password (or a Google OAuth identifier if you sign in with Google).",
        "Practice content: the audio you record, the transcripts generated from it, the feedback returned by the AI model, and any questions you save.",
        "Usage data: a daily count of how many feedback loops you've run (used to enforce the Free plan ceiling), the date of each saved question, and basic technical logs.",
        "Trial enforcement signals: a randomly generated browser identifier we store in your browser, plus your IP address held briefly server-side. We pair these to enforce the pre-sign-up trial limit on transcription. Both reset on a 24-hour rolling window.",
        "Payment data: if you subscribe to Pro, Stripe handles the card details. We receive a Stripe customer ID and subscription ID — never your full card number."
      ]
    },
    {
      id: "use",
      label: "How we use it",
      body: [
        "Run the service: transcribe your recordings, generate written feedback, save questions you ask us to save, enforce your plan's daily limits.",
        "Bill you: process Pro subscriptions, send receipts, handle cancellations.",
        "Improve the product: aggregated, de-identified usage patterns help us understand which practice modes get used and where the loop breaks down. We do not use your individual recordings or transcripts to train AI models.",
        "Reach you when needed: confirmation emails, billing notices, security alerts. We don't send marketing email without your consent."
      ]
    },
    {
      id: "third-parties",
      label: "Third parties we share with",
      body: [
        "OpenAI: your audio is sent to Whisper for transcription, and your transcript is sent to GPT-4 for feedback generation. OpenAI processes this data under their API terms and does not use it to train their models by default.",
        "Stripe: handles all payment processing. Card details go directly to Stripe; we never see them.",
        "Amazon Web Services (AWS): hosts the application, stores your account and recordings via Cognito and S3, and routes API traffic. AWS is contractually bound to keep your data private.",
        "Google: only if you choose Sign in with Google. Google receives a request to authenticate you and shares your basic profile (email, name) back with us."
      ]
    },
    {
      id: "retention",
      label: "How long we keep it",
      body: [
        "Active account data is kept while your account exists. If you delete your account, recordings, transcripts, feedback, and saved questions are removed within 30 days, with the following exceptions:",
        "Billing records are retained as long as required for accounting and tax compliance (typically seven years).",
        "Anonymized, aggregated analytics may persist indefinitely."
      ]
    },
    {
      id: "rights",
      label: "Your rights",
      body: [
        "Access: ask us for a copy of the data tied to your account.",
        "Delete: delete your account from the profile page, or email us to request a manual deletion.",
        "Correct: edit your name, email, and saved questions in-app at any time.",
        "Withdraw consent: you can revoke Google OAuth access from your Google account settings.",
        "If you're in the EU, UK, or California, you have additional rights under GDPR and CCPA — including the right to file a complaint with your data protection authority."
      ]
    },
    {
      id: "cookies",
      label: "Cookies and tracking",
      body: [
        "We use the minimum identifiers needed to run the app: an authentication cookie set by AWS Cognito for signed-in sessions, and a randomly generated browser identifier we keep in your browser's local storage (it's used as a key for the pre-sign-up trial limit, nothing else).",
        "On the server side, we briefly hold your IP address paired with that browser identifier for up to 24 hours so the free-trial transcription limit is enforced in one place. After 24 hours the pairing is dropped from our cache.",
        "We don't use third-party advertising or behavioral tracking cookies, we don't sell or share IP addresses, and we don't use the trial signals for any other purpose."
      ]
    },
    {
      id: "children",
      label: "Children",
      body: [
        "The service is not directed at children under 13. If we learn we've collected data from a child under 13, we'll delete it."
      ]
    },
    {
      id: "changes",
      label: "Changes to this policy",
      body: [
        "If we change something material, we'll let you know in-app or by email. The 'last updated' date at the top reflects the latest revision."
      ]
    },
    {
      id: "contact",
      label: "Contact",
      body: [
        "Privacy questions or data requests: privacy@interviewlab.co."
      ]
    }
  ]
};

const LegalPage = ({ kind }) => {
  const data = kind === "privacy" ? PRIVACY : TERMS;
  const otherPath = kind === "privacy" ? "/terms" : "/privacy";
  const otherLabel = kind === "privacy" ? "Terms" : "Privacy Policy";

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: CANVAS,
        minHeight: "100vh",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3 },
        textAlign: "left",
        overflow: "hidden"
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 45% at 80% -8%, rgba(228, 222, 207, 0.45), transparent 65%), radial-gradient(ellipse 50% 40% at 14% 6%, rgba(56, 84, 232, 0.06), transparent 72%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0) 55%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0) 55%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />

      <Container
        maxWidth="md"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
        component={motion.div}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 110, damping: 22 }}
      >
        <Box sx={{ mb: { xs: 5, md: 7 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5
            }}
          >
            <Box
              component={motion.span}
              animate={{ opacity: [0.55, 1, 0.55] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: ACCENT
              }}
            />
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.7rem",
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_MUTED
              }}
            >
              {data.kicker}
            </Typography>
          </Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3rem" },
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              color: INK,
              m: 0,
              mb: 2,
              maxWidth: "20ch"
            }}
          >
            {data.title}
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: { xs: "1rem", md: "1.06rem" },
              lineHeight: 1.55,
              color: INK_SOFT,
              m: 0,
              mb: 3,
              maxWidth: "55ch"
            }}
          >
            {data.lede}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap"
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.68rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT
              }}
            >
              Last updated · {LAST_UPDATED}
            </Typography>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: INK_HAIRLINE
              }}
            />
            <Box
              component={Link}
              to={otherPath}
              sx={{
                fontFamily: SANS,
                fontSize: "0.86rem",
                fontWeight: 500,
                color: ACCENT,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" }
              }}
            >
              Read the {otherLabel}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "rgba(15, 17, 33, 0.02)",
            border: `1px solid ${INK_HAIRLINE}`,
            borderRadius: "16px",
            px: { xs: 2.5, md: 3 },
            py: { xs: 2, md: 2.5 },
            mb: { xs: 5, md: 7 }
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.66rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: INK_FAINT,
              mb: 1.5
            }}
          >
            Contents
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              p: 0,
              listStyle: "none",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 0.75
            }}
          >
            {data.sections.map((s, i) => (
              <Box
                component="li"
                key={s.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 1.25,
                  alignItems: "baseline"
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.7rem",
                    color: INK_FAINT,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </Typography>
                <Box
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    fontFamily: SANS,
                    fontSize: "0.92rem",
                    fontWeight: 500,
                    color: INK_SOFT,
                    textDecoration: "none",
                    transition: "color 0.15s ease",
                    "&:hover": { color: INK }
                  }}
                >
                  {s.label}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {data.sections.map((s, i) => (
            <Box
              key={s.id}
              id={s.id}
              sx={{
                borderTop: `1px solid ${INK_HAIRLINE}`,
                pt: { xs: 4, md: 5 },
                pb: { xs: 4, md: 5 },
                scrollMarginTop: 80
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 2,
                  mb: 2
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.74rem",
                    fontWeight: 500,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: ACCENT,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: { xs: "1.4rem", md: "1.65rem" },
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    color: INK,
                    m: 0
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {s.body.map((p, j) => (
                  <Typography
                    key={j}
                    sx={{
                      fontFamily: SANS,
                      fontSize: { xs: "0.98rem", md: "1.02rem" },
                      lineHeight: 1.65,
                      color: INK_SOFT,
                      maxWidth: "65ch",
                      m: 0
                    }}
                  >
                    {p}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            pt: 4,
            borderTop: `1px solid ${INK_HAIRLINE}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "0.92rem",
              color: INK_FAINT
            }}
          >
            Continue reading the {otherLabel}.
          </Typography>
          <Box
            component={Link}
            to={otherPath}
            sx={{
              fontFamily: SANS,
              fontSize: "0.92rem",
              fontWeight: 500,
              color: ACCENT,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" }
            }}
          >
            {otherLabel} →
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalPage;
